import { db } from './firestore';
import { collection, doc, getDocs, setDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { USE_CONTRACTS_V2 } from './contractsV2';
import { finesService, officeError } from './finesService';
import logger from '../utils/logger';

const COLLECTION = 'model_pricing';

// With the new store (USE_CONTRACTS_V2) the prices are the engine's office records (/api/office/model-pricing):
// the same {MODEL KEY: {daily, monthly}} map; each save replaces the model's prices (as setDoc without merge) and
// the store stamps last_updated itself. A refusal throws / answers the engine's own words (officeError).
const refused = (e) => { throw officeError(e); };

export const modelPricingService = {
    /**
     * Fetch all pricing configurations
     * Returns a map: { "NISSAN SUNNY": { daily: 60, monthly: 1500 }, ... }
     */
    getAllPricing: async () => {
        try {
            if (USE_CONTRACTS_V2) return (await finesService.officePricing())?.items || {};
            const snapshot = await getDocs(collection(db, COLLECTION));
            const pricingMap = {};
            snapshot.docs.forEach(doc => {
                pricingMap[doc.id] = doc.data();
            });
            return pricingMap;
        } catch (error) {
            logger.error("Error fetching model pricing:", error);
            return {};
        }
    },

    /**
     * Save or Update pricing for a model
     * @param {string} modelName - The model name (key)
     * @param {number} daily - Daily Rate
     * @param {number} monthly - Monthly Rate
     */
    savePricing: async (modelName, daily, monthly) => {
        if (!modelName) return;
        const normalizedKey = modelName.trim().toUpperCase();
        if (USE_CONTRACTS_V2) {
            await finesService.officePricingSave(normalizedKey, Number(daily), Number(monthly)).catch(refused);
            return;
        }
        await setDoc(doc(db, COLLECTION, normalizedKey), {
            daily: Number(daily),
            monthly: Number(monthly),
            last_updated: new Date().toISOString()
        });
    },

    /**
     * Save ALL pricing configurations in a batch
     * @param {Object} pricingMap - { "MODEL KEY": { daily: X, monthly: Y }, ... }
     */
    saveAllPricing: async (pricingMap) => {
        if (!pricingMap || Object.keys(pricingMap).length === 0) return;

        try {
            if (USE_CONTRACTS_V2) {
                // one transaction on the server: every model or none
                const prices = {};
                Object.entries(pricingMap).forEach(([modelName, rates]) => {
                    prices[modelName.trim().toUpperCase()] = { daily: Number(rates.daily || 0), monthly: Number(rates.monthly || 0) };
                });
                await finesService.officePricingBatch(prices).catch(refused);
                return { success: true };
            }
            const batch = writeBatch(db);
            const timestamp = new Date().toISOString();

            Object.entries(pricingMap).forEach(([modelName, rates]) => {
                const normalizedKey = modelName.trim().toUpperCase();
                const ref = doc(db, COLLECTION, normalizedKey);
                batch.set(ref, {
                    daily: Number(rates.daily || 0),
                    monthly: Number(rates.monthly || 0),
                    last_updated: timestamp
                });
            });

            await batch.commit();
            return { success: true };
        } catch (error) {
            logger.error("Error batch saving model pricing:", error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Delete pricing for a model
     */
    deletePricing: async (modelName) => {
        if (!modelName) return;
        const normalizedKey = modelName.trim().toUpperCase();
        if (USE_CONTRACTS_V2) {
            // a price already gone is what the delete wanted (Firestore's deleteDoc of a missing doc succeeds too)
            await finesService.officePricingDelete(normalizedKey).catch((e) => {
                if (e?.response?.status !== 404) refused(e);
            });
            return;
        }
        await deleteDoc(doc(db, COLLECTION, normalizedKey));
    }
};
